import { Injectable, Injector } from '@angular/core'
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http'
import { Router } from '@angular/router'
import { Observable, throwError } from 'rxjs'
import { ToastrService } from 'ngx-toastr'
import { SessionService } from '../services/session.service'
import { tap, catchError } from 'rxjs/operators'
import { AUTH_STORE_KEY } from '../constants/constants'
import { decryptData } from '@/app/helpers/crypto.util'

@Injectable()
export class TokenInterceptorService implements HttpInterceptor {
  constructor(private injector: Injector) {}
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const sessionService = this.injector.get(SessionService)
    const toastr = this.injector.get(ToastrService)

    let accessToken = null
    let rawAuthData = localStorage.getItem(AUTH_STORE_KEY)

    if (rawAuthData) {
      try {
        let authInfo
        if (rawAuthData.startsWith('U2FsdGVkX1')) {
          const decryptedData = decryptData(rawAuthData)

          if (typeof decryptedData === 'string') {
            authInfo = JSON.parse(decryptedData)
          } else {
            authInfo = decryptedData
          }
        } else {
          authInfo = JSON.parse(rawAuthData)
        }

        if (authInfo && authInfo.access_token) {
          accessToken = authInfo.access_token
        }
      } catch (error) {
        console.error('Failed to decrypt or parse auth info:', error)
        localStorage.removeItem(AUTH_STORE_KEY)
        this.logoutV2(
          sessionService,
          'Authentication data corrupted. Please login again.'
        )
        throw new Error(`Failed to decrypt authentication data`)
      }
    }

    if (this.isInvalidToken(accessToken)) {
      this.logoutV2(sessionService, 'You are not logged in')
      throw new Error(`Access token not found`)
    }

    return next.handle(this.customRequest(request, accessToken)).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          return event
        }
        return ''
      }),
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            this.logoutV2(
              sessionService,
              'Your session has expired. Please login again',
              true
            )
          } else if (
            error.status === 403 &&
            error.error?.error === 'IP_NOT_WHITELISTED'
          ) {
            const message =
              error.error.message ||
              'Your IP address is not authorized to access this resource. Please contact your administrator.'
            toastr.error(message, 'Access Denied', {
              timeOut: 0, // Don't auto-dismiss
              extendedTimeOut: 0,
              closeButton: true,
              tapToDismiss: false,
            })
            // Optionally logout the user
            this.logoutV2(sessionService, message)
          }
        }
        return throwError(() => {
          return error
        })
      })
    )
  }

  isInvalidToken(token: string) {
    return (
      typeof token === 'undefined' ||
      token === null ||
      token.toString().length === 0
    )
  }

  customRequest(request: HttpRequest<any>, accessToken: string) {
    const req = request.clone({
      setHeaders: {
        Authorization: 'Bearer {0}'.replace('{0}', accessToken),
      },
    })
    return req
  }

  logoutV2(
    sessionService: SessionService,
    logoutMessage: string,
    hasSessionExpired: boolean = false
  ) {
    const router = this.injector.get(Router)
    sessionService.redirectUrl = router.url
    sessionService.logoutMessage = logoutMessage
    sessionService.hasSessionExpired = hasSessionExpired
    sessionService.logoutV2()
  }
}
